import { client } from "@/client";
import { CostDetails, ShiftType } from "@/graphql/__generated__/graphql";

/**
 * Combines the existing and new cost details by summing or subtracting their respective values based on the multiplier.
 * @param {Array<CostDetails>} existingCosts - The existing cost details.
 * @param {object} newCosts - The new cost details.
 * @param {number} multiplier - The multiplier for the operation (1 for add, -1 for subtract).
 * @returns {object} - The combined cost details.
 */
export const combineCostDetails = (existingCosts: Array<CostDetails>, newCosts: object, multiplier: number): object => {
  const excludedFields = ["__typename", "id", "type"];
  const combinedCosts = {};

  Object.keys(existingCosts).forEach((key) => {
    if (!excludedFields.includes(key) && newCosts[key] !== undefined) {
      combinedCosts[key] = (
        parseFloat(existingCosts[key]) + parseFloat(newCosts[key]) * multiplier
      ).toString();
    } else {
      combinedCosts[key] = existingCosts[key];
    }
  });

  return combinedCosts;
};

/**
 * Helper function to update the cost details of a Tender in the cache.
 * @param {number} tenderId - The ID of the tender to update in the cache.
 * @param {Object} newCostDetails - The new cost details to update the cache with.
 * @param {string} operation - The operation to perform on the cost details.
 */
export const updateCostDetails = (tenderId: number, newCostDetails: CostDetails[], operation: "Add" | "Subtract") => {
  client.cache.modify({
    id: client.cache.identify({ __typename: "Tender", id: tenderId }),
    fields: {
      costDetails(existingCostDetails = []) {
        if (!existingCostDetails) {
          return newCostDetails;
        }

        // Combine existing and new cost details based on the operation
        const updatedCostDetails = existingCostDetails.map((existingCostDetail: { type: ShiftType; payCosts: CostDetails[]; materialCosts: CostDetails[]; specialCosts: CostDetails[]; subcontractorCosts: string; totalCosts: string; minutes: number; }) => {
          const newCostDetail = newCostDetails.find((costDetail) => costDetail.type === existingCostDetail.type);
          if (newCostDetail) {
            const operationMultiplier = operation === "Add" ? 1 : -1;
            return {
              ...existingCostDetail,
              payCosts: combineCostDetails(existingCostDetail.payCosts, newCostDetail.payCosts, operationMultiplier),
              materialCosts: combineCostDetails(existingCostDetail.materialCosts, newCostDetail.materialCosts, operationMultiplier),
              subcontractorCosts: (parseFloat(existingCostDetail.subcontractorCosts) + parseFloat(newCostDetail.subcontractorCosts.toString()) * operationMultiplier).toString(),
              totalCosts: (parseFloat(existingCostDetail.totalCosts) + parseFloat(newCostDetail.totalCosts.toString()) * operationMultiplier).toString(),
              minutes: existingCostDetail.minutes + newCostDetail.minutes * operationMultiplier,
            };
          }
          return existingCostDetail;
        });
        return updatedCostDetails;
      },
    },
  });
};

/**
 * Extracts the arguments object from a 'storeFieldName' string.
 *
 * @param storeFieldName - The field name from the Apollo cache, which includes
 *                         the field name and serialized arguments.
 * @returns The parsed arguments object or null if parsing fails.
 */
export const getArgumentsFromStoreFieldName = (storeFieldName: string) => {
  // Find the position of the opening '(' and closing ')' parentheses
  const argsStart = storeFieldName.indexOf("(");
  const argsEnd = storeFieldName.lastIndexOf(")");

  // If either parenthesis is not found, return null (no arguments to extract)
  if (argsStart === -1 || argsEnd === -1) return null;

  // Extract the substring containing the arguments (between the parentheses)
  const argsString = storeFieldName.slice(argsStart + 1, argsEnd);

  try {
    // Parse the arguments string into a JavaScript object
    return JSON.parse(argsString);
  } catch (error) {
    // Log an error if parsing fails and return null
    console.error("Failed to parse arguments:", error);
    return null;
  }
};
